
import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Check, Heart, Plus, ShieldCheck, Smile, Sparkles, Sun } from "lucide-react";
import { Stagger, StaggerItem } from "@/components/FadeIn";
import { M3Card, M3CardHeader } from "@/components/M3Shared";
import { MemberAvatar } from "@/components";
import { cn } from "@/lib/utils";
import { useCurrentUser } from "@/lib/useCurrentUser";
import { useCurrentFamily } from "@/lib/useCurrentFamily";
import { useSupabaseQuery, useRealtimeTable } from "@/lib/dbHooks";
import { supabase } from "@/lib/supabase";
import type { Database } from "@/types/db";


/* ─────────────────────  Types  ───────────────────── */

type CheckinState = "pending" | "done" | "missed";

type PastCheckin = {
  checked_on: string;
  checked_at: string;
  state: "done" | "pending" | "missed";
  spirit: number | null;
  body: number | null;
  mood: number | null;
};

type MemberCheckin = {
  id: string;
  name: string;
  relation: string;
  initials: string;
  color: string;
  state: CheckinState;
  checkedAt?: string;
  wellness?: { spirit: number; body: number; mood: number };
  /** Public Storage URL of the user's profile picture. Populated
   *  from `me0.avatarUrl` when this row IS the signed-in user's
   *  own family_member row; null for other members. */
  avatarUrl?: string | null;
  /* Per-day state for the 7-day red-dot strip. `1` = done, `0` =
     missed (no row at all OR a row with state != done). Index 0
     is the oldest day in the strip; last index is today. The
     active window covers the last 7 days, so every cell in the
     strip is inside the window — there's no "outside" branch
     on the user-visible strip. */
  dotStrip: Array<0 | 1>;
  /* Full 30-day history of this member's check-ins, ascending by
     date, used by the "過往記錄" sheet. RLS-gated; viewer may see
     only rows they're authorised to read. */
  pastCheckIns: PastCheckin[];
};

const wellnessLabels = [
  { value: 1, label: "差", color: "bg-rose-500" },
  { value: 2, label: "一般", color: "bg-amber-500" },
  { value: 3, label: "可以", color: "bg-lime-500" },
  { value: 4, label: "好", color: "bg-emerald-500" },
  { value: 5, label: "很好", color: "bg-teal-500" },
] as const;

// Three wellness axes shown in the check-in sheet. Lifted out of the
// JSX so the picker list is data-driven (the wellnessLabels values
// above are the rating buttons; this is the axis metadata that pairs
// each axis with its lucide icon and label).
const WELLNESS_AXES = [
  { key: "spirit", label: "精神", icon: Sparkles },
  { key: "body",   label: "體力", icon: Sun },
  { key: "mood",   label: "心情", icon: Heart },
] as const;

// Length of the rolling presence window. Drives both the SQL `.gte()`
// lower bound (7 days back) and the column count of the grid below.
const PRESENCE_WINDOW_DAYS = 7;

// How far back the "past scores" history card shows. The 7-day grid
// is the at-a-glance summary; this is the long-form view where
// individual rows are visible and scores per day are readable.
const HISTORY_WINDOW_DAYS = 30;
// Last day index in the 7-day grid (column 6 == "今日"). Extracted so
// the header (`Array.from({ length: PRESENCE_WINDOW_DAYS })`) and the
// "今日" label share the same magic number without drift.
const PRESENCE_TODAY_INDEX = PRESENCE_WINDOW_DAYS - 1;
// Mood log (formerly FamMood). One row per author per entry — not
// one-per-day; users can log multiple moods (e.g. 疲倦 now, 感恩 at
// dinner). Schema in migration 00500 (`public.mood_entries`). The
// catalogue MUST stay aligned with `public.mood_kind` enum.
const MOODS: { id: MoodId; emoji: string; label: string }[] = [
  { id: "happy",    emoji: "😊", label: "開心" },
  { id: "calm",     emoji: "😌", label: "平靜" },
  { id: "grateful", emoji: "🥰", label: "感恩" },
  { id: "excited",  emoji: "🤩", label: "期待" },
  { id: "tired",    emoji: "😴", label: "疲倦" },
  { id: "anxious",  emoji: "😟", label: "擔心" },
  { id: "sad",      emoji: "😔", label: "失落" },
  { id: "angry",    emoji: "😤", label: "嬲" },
];
const MOOD_BY_ID: Record<string, (typeof MOODS)[number]> =
  Object.fromEntries(MOODS.map((m) => [m.id, m]));

// How many recent entries to show in the mood feed. The query is
// also `.limit(20)` below so this constant only drives the *render* —
// having a separate number lets us raise the limit cheaply later
// without changing the visible cap.
const MOOD_FEED_LIMIT = 10;

type CheckInRow = Database["public"]["Tables"]["check_ins"]["Row"];
type MoodEntryRow = Database["public"]["Tables"]["mood_entries"]["Row"];
type MoodId =
  | "happy" | "calm" | "grateful" | "tired"
  | "anxious" | "sad" | "angry" | "excited";

export function FamStatusView() {
  const me0 = useCurrentUser();
  const cf = useCurrentFamily(me0.user?.id ?? null);
  const memberIdsKey = cf.members.map((m) => m.id).join(",");

  // Today's check-ins.
  const checkIns = useSupabaseQuery<CheckInRow[]>(async () => {
    if (!cf.members.length) return { data: [], error: null };
    const today = new Date().toISOString().slice(0, 10);
    return await supabase
      .from("check_ins")
      .select("*")
      .in("member_id", cf.members.map((m) => m.id))
      .eq("checked_on", today);
  }, [memberIdsKey]);


  useRealtimeTable<"check_ins">("check_ins", null, () => checkIns.refresh());

  // Mood log — last `MOOD_FEED_LIMIT * 2` entries so the JSX
  // `.slice(0, MOOD_FEED_LIMIT)` doesn't fight a tight limit if we
  // ever raise the display cap. Auth-grantee policy (migration
  // 01400) restricts SELECT to the author + people they've
  // explicitly shared "mood" with, so the result is per-viewer.
  const moods = useSupabaseQuery<MoodEntryRow[]>(async () => {
    if (!cf.members.length) return { data: [], error: null };
    return await supabase
      .from("mood_entries")
      .select("*")
      .in("author_member_id", cf.members.map((m) => m.id))
      .order("created_at", { ascending: false })
      .limit(MOOD_FEED_LIMIT * 2);
  }, [memberIdsKey]);

  useRealtimeTable<"mood_entries">("mood_entries", null, () => moods.refresh());

  // 7-day presence grid AND 30-day history list. `historyAgo` is the
  // SQL lower bound for both — one query serves two views so the
  // "what was I doing 3 weeks ago?" panel doesn't trigger a second
  // round trip on mount. The 7-day grid is just a `.slice(0, 7)` of
  // the same per-member day array.
  const historyAgo = (() => {
    const d = new Date();
    d.setDate(d.getDate() - (HISTORY_WINDOW_DAYS - 1));
    return d.toISOString().slice(0, 10);
  })();
  const historyCheckIns = useSupabaseQuery<CheckInRow[]>(async () => {
    if (!cf.members.length) return { data: [], error: null };
    return await supabase
      .from("check_ins")
      .select("*")
      .in("member_id", cf.members.map((m) => m.id))
      .gte("checked_on", historyAgo)
      .order("checked_on", { ascending: true });
  }, [memberIdsKey]);
  // Build the per-member day array ONCE, then slice it. The grid
  // takes the last PRESENCE_WINDOW_DAYS, the per-member red-dot
  // strip takes the same, and the past-scores list takes the full
  // HISTORY_WINDOW_DAYS.
  const presenceByMember = useMemo(() => {
    const days: string[] = [];
    for (let i = 0; i < HISTORY_WINDOW_DAYS; i++) {
      const d = new Date();
      d.setDate(d.getDate() - (HISTORY_WINDOW_DAYS - 1 - i));
      days.push(d.toISOString().slice(0, 10));
    }
    return cf.members.map((m) =>
      days.map((d) => {
        const c = (historyCheckIns.data ?? []).find(
          (x) => x.member_id === m.id && x.checked_on === d,
        );
        // `1` = done, `0` = missed (no row OR a row with a
        // non-done state). The 7-day grid and the per-card dot
        // strip both treat `0` as the red colour; the previous
        // version collapsed "no row at all" into `null`, which
        // silently rendered every missed day as a hollow border
        // — the red branch was effectively dead because the app
        // only ever writes `state = 'done'` on check-in.
        if (!c) return 0 as const;
        return c.state === "done" ? (1 as const) : (0 as const);
      }),
    );
  }, [cf.members, historyCheckIns.data]);
  // Last PRESENCE_WINDOW_DAYS columns of the same array — keeps the
  // 7-day grid visually identical to the previous behaviour.
  const presence7d = useMemo(
    () => presenceByMember.map((row) => row.slice(-PRESENCE_WINDOW_DAYS)),
    [presenceByMember],
  );

  const members = useMemo(
    () =>
      cf.members.map((m, idx) => {
        const c = (checkIns.data ?? []).find((x) => x.member_id === m.id);
        // The red-dot strip is the *last 7 days* of the long
        // presenceByMember row — same data the grid below shows,
        // just rendered inline on the card so a missing day is
        // obvious without scrolling to the grid.
        const dotStrip = (presenceByMember[idx] ?? []).slice(
          -PRESENCE_WINDOW_DAYS,
        );
        // Past check-ins for the "view history" sheet. `historyCheckIns`
        // is already ordered ascending by date so the user sees
        // the oldest at the top — but the list UI sorts descending
        // before render so they read newest-first.
        const past = (historyCheckIns.data ?? [])
          .filter((x) => x.member_id === m.id)
          .map((x) => ({
            checked_on: x.checked_on,
            checked_at: x.checked_at,
            state: x.state,
            spirit: x.wellness_spirit ?? null,
            body: x.wellness_body ?? null,
            mood: x.wellness_mood ?? null,
          }));
        return {
          id: m.id,
          name: m.display_name,
          relation: m.relation_label || m.relation,
          initials: m.initials,
          color: m.avatar_color,
          state: c?.state === "done" ? ("done" as const) : ("pending" as const),
          // Global avatar flows only for the signed-in user's
          // own row; other members stay on the family_members
          // gradient+initials.
          avatarUrl: m.id === cf.self?.id ? (me0.avatarUrl ?? null) : null,
          checkedAt: c?.checked_at
            ? new Intl.DateTimeFormat("zh-HK", {
                hour: "2-digit",
                minute: "2-digit",
              }).format(new Date(c.checked_at))
            : undefined,
          wellness: c
            ? {
                spirit: c.wellness_spirit ?? 3,
                body: c.wellness_body ?? 3,
                mood: c.wellness_mood ?? 3,
              }
            : undefined,
          dotStrip,
          pastCheckIns: past,
        };
      }),
    [cf.members, checkIns.data, presenceByMember, historyCheckIns.data, me0.avatarUrl],
  );

  // Find this viewer's row in the members list by id, not by array
  // position. `cf.members` is ordered by `display_name asc`, so
  // `members[0]` is whoever sorts first alphabetically — usually
  // the user (because "你" / "Me" tends to win CJK / default
  // collations) but **fragile**: rename yourself to a name that
  // sorts later, or add a family member whose name sorts first,
  // and the "I just 打卡'd" badge moves to the wrong card. Match
  // `cf.self.id` instead. Fallback to `members[0]` keeps the
  // pre-bootstrap loading state (when self has resolved but the
  // members query hasn't) from crashing the render.
  const me = members.find((m) => m.id === cf.self?.id) ?? members[0];
  const meChecked = me?.state === "done";
  const [pendingWellness, setPendingWellness] = useState({
    spirit: 3 as 1 | 2 | 3 | 4 | 5,
    body: 3 as 1 | 2 | 3 | 4 | 5,
    mood: 3 as 1 | 2 | 3 | 4 | 5,
  });
  const [showWellness, setShowWellness] = useState(false);
  // Surface for checkIn() upsert errors. Without this a silent RLS
  // denial makes the button feel like it works when it actually
  // didn't, and the "I just 打卡'd" badge never flips to done.
  const [uploadError, setUploadError] = useState<string | null>(null);
  // Mood picker (formerly the standalone FamMood sheet). State lives
  // here so the new "記錄心情" button can drive it from inside
  // FamStatusView without lifting a sub-component out.
  const [moodPickerOpen, setMoodPickerOpen] = useState(false);
  const [moodDraft, setMoodDraft] = useState<{ id: MoodId | null; note: string }>({
    id: null,
    note: "",
  });
  const [moodSubmitting, setMoodSubmitting] = useState(false);
  // Same rationale as `uploadError` — without surfacing the failure
  // the user taps the button and nothing visually happens.
  const [moodUploadError, setMoodUploadError] = useState<string | null>(null);
  // Member id whose past scores sheet is open (null = closed). Tapping
  // a family card in "家人打卡動態" opens it; tapping the backdrop or
  // the close button clears it.
  const [historySheetMemberId, setHistorySheetMemberId] = useState<
    string | null
  >(null);

  if (!me) {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">
        載入中…
      </div>
    );
  }

  const openEdit = (): void => {
    if (me.wellness) {
      const w = me.wellness;
      setPendingWellness({
        spirit: w.spirit as 1 | 2 | 3 | 4 | 5,
        body: w.body as 1 | 2 | 3 | 4 | 5,
        mood: w.mood as 1 | 2 | 3 | 4 | 5,
      });
    }
    setShowWellness(true);
  };

  const checkIn = async (): Promise<void> => {
    if (!cf.self) return;
    // Capture the upsert error and surface it; otherwise a silent
    // RLS denial (e.g. the user's `family_members` row was deleted
    // out from under us, or the unique constraint is violated) makes
    // the button feel like it works when it actually didn't — and
    // the "I just 打卡'd" badge never flips to done.
    const { error } = await supabase.from("check_ins").upsert(
      {
        member_id: cf.self.id,
        checked_on: new Date().toISOString().slice(0, 10),
        state: "done",
        wellness_spirit: pendingWellness.spirit,
        wellness_body: pendingWellness.body,
        wellness_mood: pendingWellness.mood,
      },
      {
        // The table's uniqueness is on (member_id, checked_on)
        // — see migration 00600 — not the primary key. Without
        // an onConflict, supabase-js falls back to the PK and
        // every call becomes an INSERT; the second 打卡 of the
        // day then fails with 23505
        // ('check_ins_member_id_checked_on_key').
        onConflict: "member_id,checked_on",
      },
    );
    if (error) {
      setUploadError(error.message);
      return;
    }
    setUploadError(null);
    checkIns.refresh();
    setShowWellness(false);
  };

  const logMood = async (): Promise<void> => {
    if (!moodDraft.id || !cf.self) return;
    setMoodSubmitting(true);
    // mood_entries has no unique-key constraint to fight (one row
    // per submission), so a plain INSERT is correct here — unlike
    // check_ins above. The onConflict lesson learned from that path
    // was that the table HAS to match the call, so this comment is
    // load-bearing for the next person to touch it.
    const { error } = await supabase.from("mood_entries").insert({
      author_member_id: cf.self.id,
      mood: moodDraft.id,
      note: moodDraft.note.trim() || null,
    });
    setMoodSubmitting(false);
    if (error) {
      setMoodUploadError(error.message);
      return;
    }
    setMoodUploadError(null);
    setMoodDraft({ id: null, note: "" });
    setMoodPickerOpen(false);
    moods.refresh();
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <motion.header
        className="px-1 pt-1"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
      >
        <p className="text-xs text-muted-foreground sm:text-sm">
          FamStatus · 每日安心打卡
        </p>
        <h1 className="mt-1 font-serif text-2xl font-semibold tracking-tight sm:text-3xl lg:text-4xl">
          {meChecked ? "今日已完成 ✅" : "今日未打卡"}
        </h1>
        <p className="mt-2 text-pretty text-sm text-muted-foreground sm:text-base">
          {meChecked
            ? `${me.name} 已經同屋企人講「我好好」。家人收到訊息，無需擔心。`
            : "輕輕一撳，屋企人就收到你嘅平安。"}
        </p>
      </motion.header>
      {uploadError && (
        <div role="alert" className="flex items-start gap-2 rounded-2xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
          <span className="flex-1 leading-snug">打卡失敗：{uploadError}</span>
          <button type="button" onClick={() => setUploadError(null)} aria-label="關閉錯誤"
            className="rounded p-0.5 text-rose-500 hover:bg-rose-100">
            ×
          </button>
        </div>
      )}

      {/* Check-in card */}
      <M3Card>
        <div className="flex items-center gap-4">
          <MemberAvatar
            initials={me.initials}
            color={me.color}
            avatarUrl={me.avatarUrl ?? null}
            size="xl"
            rounded="lg"
          />
          <div className="min-w-0 flex-1">
            <div className="text-[11px] text-muted-foreground">{me.relation}</div>
            <div className="truncate text-base font-semibold">{me.name}</div>
            <div className="mt-0.5 text-xs text-muted-foreground">
              {meChecked
                ? `已於 ${me.checkedAt ?? "剛剛"} 打卡`
                : "今日仲未打卡"}
            </div>
          </div>
        </div>

        <AnimatePresence mode="wait" initial={false}>
          {showWellness ? (
            <motion.div
              key="wellness"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="mt-4 space-y-3 overflow-hidden border-t border-border/60 pt-4"
            >
              {WELLNESS_AXES.map(({ key, label, icon: Icon }) => (
                <div key={key}>
                  <div className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-foreground/85">
                    <Icon className="h-3.5 w-3.5 text-primary" aria-hidden />
                    {label}
                  </div>
                  <div className="flex gap-1.5">
                    {wellnessLabels.map((opt) => {
                      const active = pendingWellness[key] === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() =>
                            setPendingWellness((p) => ({
                              ...p,
                              [key]: opt.value as 1 | 2 | 3 | 4 | 5,
                            }))
                          }
                          className={cn(
                            "flex h-10 flex-1 items-center justify-center rounded-full border text-xs font-medium transition-colors",
                            active
                              ? "border-primary bg-primary/10 text-primary"
                              : "border-border/60 bg-card text-foreground/75 hover:bg-secondary",
                          )}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
              <p className="text-[11px] text-muted-foreground">
                呢個評分預設只係你自己睇到。你可以喺「設定」選擇分享畀指定家人。
              </p>
              {meChecked ? (
                <div className="flex gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => setShowWellness(false)}
                    className="inline-flex h-11 flex-1 items-center justify-center rounded-full border border-border/60 bg-card text-sm font-medium text-foreground/85 hover:bg-secondary"
                  >
                    取消
                  </button>
                  <motion.button
                    type="button"
                    onClick={checkIn}
                    whileTap={{ scale: 0.96 }}
                    transition={{ type: "spring", stiffness: 400, damping: 22 }}
                    className="inline-flex h-11 flex-1 items-center justify-center gap-1.5 rounded-full bg-primary text-sm font-semibold text-primary-foreground shadow-sm shadow-primary/30"
                  >
                    <Check className="h-4 w-4" aria-hidden />
                    更新評分
                  </motion.button>
                </div>
              ) : null}
            </motion.div>
          ) : null}
        </AnimatePresence>

        <div className="mt-4 flex gap-2">
          {!meChecked ? (
            <motion.button
              type="button"
              onClick={checkIn}
              whileTap={{ scale: 0.96 }}
              whileHover={{ scale: 1.02 }}
              transition={{ type: "spring", stiffness: 400, damping: 22 }}
              className="inline-flex h-12 flex-1 items-center justify-center gap-2 rounded-full bg-primary px-5 text-sm font-semibold text-primary-foreground shadow-sm shadow-primary/30"
            >
              <Check className="h-5 w-5" aria-hidden />
              我今日好好
            </motion.button>
          ) : (
            <button
              type="button"
              onClick={openEdit}
              className="inline-flex h-12 flex-1 items-center justify-center rounded-full border border-border/60 bg-card px-5 text-sm font-medium text-foreground/85 hover:bg-secondary"
            >
              編輯評分
            </button>
          )}
        </div>
      </M3Card>

      {/* Family presence timeline */}
      <M3Card>
        <M3CardHeader
          title="家人打卡動態"
          subtitle={`今日 · ${members.length} 位家庭成員`}
          trailing={`${members.filter((m) => m.state === "done").length} / ${members.length} 已打卡`}
        />
        <Stagger as="ul" className="mt-3 space-y-2.5" gap={0.06}>
          {members.map((m) => {
            const missedDays = m.dotStrip.filter((c) => c === 0).length;
            return (
              <StaggerItem
                key={m.id}
                as="li"
                className="flex items-center gap-3 rounded-2xl border border-border/40 bg-card p-3"
              >
                <MemberAvatar
                  initials={m.initials}
                  color={m.color}
                  avatarUrl={m.avatarUrl ?? null}
                  size="lg"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                    <span
                      className={cn(
                        "h-1.5 w-1.5 rounded-full",
                        m.state === "done" && "bg-emerald-500",
                        m.state === "pending" && "bg-amber-500",
                      )}
                      aria-hidden
                    />
                    {m.relation}
                  </div>
                  <button
                    type="button"
                    onClick={() => setHistorySheetMemberId(m.id)}
                    className="-mx-1 block w-[calc(100%+0.5rem)] truncate rounded px-1 text-left text-sm font-medium hover:bg-secondary/60"
                    aria-label={`查看 ${m.name} 過去打卡記錄`}
                  >
                    {m.name}
                  </button>
                  <div className="mt-1 flex items-center gap-2">
                    <div
                      className="flex items-center gap-1"
                      role="list"
                      aria-label="過去 7 日打卡"
                    >
                      {m.dotStrip.map((c, i) => {
                        const isToday = i === m.dotStrip.length - 1;
                        return (
                          <span
                            key={i}
                            role="listitem"
                            aria-label={
                              isToday
                                ? c === 1
                                  ? "今日已打卡"
                                  : "今日未打卡"
                                : c === 1
                                  ? "已打卡"
                                  : "缺勤"
                            }
                            title={
                              isToday
                                ? c === 1
                                  ? "今日已打卡"
                                  : "今日未打卡"
                                : c === 1
                                  ? "已打卡"
                                  : "缺勤"
                            }
                            className={cn(
                              "h-1.5 w-1.5 rounded-full ring-1 ring-inset",
                              c === 1 && "bg-emerald-500 ring-emerald-500/30",
                              c === 0 && "bg-rose-500 ring-rose-500/40",
                              isToday && "ring-2",
                            )}
                          />
                        );
                      })}
                    </div>
                    {missedDays > 0 && m.state !== "done" && (
                      <span className="rounded-full bg-rose-50 px-1.5 py-0.5 text-[10px] font-medium text-rose-700">
                        近 7 日缺 {missedDays} 日
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => setHistorySheetMemberId(m.id)}
                      className="ml-auto text-[10px] font-medium text-primary hover:underline"
                      aria-label={`查看 ${m.name} 過去打卡記錄`}
                    >
                      過往記錄 →
                    </button>
                  </div>
                </div>
                {m.state === "done" && (
                  <span className="shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                    已打卡
                  </span>
                )}
              </StaggerItem>
            );
          })}
        </Stagger>
      </M3Card>
      {/* 7-day presence grid */}
      <M3Card>
        <M3CardHeader title="過去 7 日" subtitle="綠 = 打卡 · 紅 = 缺勤" />
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[360px] border-separate border-spacing-1.5 text-center">
            <thead>
              <tr>
                <th className="w-20" />
                {Array.from({ length: PRESENCE_WINDOW_DAYS }).map((_, i) => {
                  const d = new Date();
                  d.setDate(d.getDate() - (PRESENCE_WINDOW_DAYS - 1 - i));
                  return (
                    <th
                      key={i}
                      className="text-[10px] font-medium text-muted-foreground"
                    >
                      {i === PRESENCE_TODAY_INDEX ? "今日" : `${d.getMonth() + 1}/${d.getDate()}`}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {members.map((m, row) => (
                <tr key={m.id}>
                  <td className="pr-2 text-left text-[11px] font-medium text-foreground/85">
                    {m.relation}
                  </td>
                  {(presence7d[row] ?? []).map((cell, i) => (
                    <td key={i}>
                      <span
                        aria-hidden
                        aria-label={cell === 1 ? "已打卡" : "缺勤"}
                        title={cell === 1 ? "已打卡" : "缺勤"}
                        className={cn(
                          "mx-auto block h-5 w-5 rounded-md",
                          cell === 1 && "bg-emerald-500/85",
                          cell === 0 && "bg-rose-500/85",
                        )}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </M3Card>

      {/* Mood log (formerly FamMood). Latest 10 entries from anyone in
          the family. RLS handles visibility; the UI is read-only here. */}
      <M3Card>
        <M3CardHeader
          title="心情動態"
          subtitle="你想講嘅一句心底話"
          trailing={
            <motion.button
              type="button"
              onClick={() => setMoodPickerOpen(true)}
              whileTap={{ scale: 0.96 }}
              whileHover={{ scale: 1.02 }}
              transition={{ type: "spring", stiffness: 400, damping: 22 }}
              className="inline-flex h-9 items-center gap-1 rounded-full bg-primary px-3 text-xs font-medium text-primary-foreground shadow-sm shadow-primary/30"
              aria-label="記錄心情"
            >
              <Plus className="h-3.5 w-3.5" aria-hidden />
              記錄心情
            </motion.button>
          }
        />
        {moodUploadError && (
          <div role="alert" className="mt-2 flex items-start gap-2 rounded-2xl border border-rose-200 bg-rose-50 px-3 py-2 text-[11px] text-rose-700">
            <span className="flex-1 leading-snug">記錄失敗：{moodUploadError}</span>
            <button type="button" onClick={() => setMoodUploadError(null)} aria-label="關閉錯誤"
              className="rounded p-0.5 text-rose-500 hover:bg-rose-100">
              ×
            </button>
          </div>
        )}
        {((moods.data ?? []).length === 0 && !moods.loading) ? (
          <p className="mt-2 text-sm text-muted-foreground">暫無任何心情記錄。</p>
        ) : (
          <Stagger as="ul" className="mt-3 space-y-2.5" gap={0.06}>
            {(moods.data ?? []).slice(0, MOOD_FEED_LIMIT).map((e) => {
              const author = cf.members.find((m) => m.id === e.author_member_id);
              const mood = MOOD_BY_ID[e.mood];
              const isMine = e.author_member_id === cf.self?.id;
              return (
                <StaggerItem
                  key={e.id}
                  as="li"
                  className="flex items-start gap-3 rounded-2xl border border-border/40 bg-card p-3"
                >
                  <MemberAvatar
                    initials={author?.initials ?? "·"}
                    color={author?.avatar_color ?? "from-zinc-300 to-zinc-500"}
                    avatarUrl={isMine ? (me0.avatarUrl ?? null) : null}
                    size="md"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                      <span className="font-medium text-foreground/85">
                        {author?.display_name ?? "家人"}
                      </span>
                      <span>·</span>
                      <span>{author?.relation_label ?? ""}</span>
                      {isMine && (
                        <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">
                          我
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 text-sm font-medium">
                      <span aria-hidden className="mr-1">{mood?.emoji ?? "•"}</span>
                      {mood?.label ?? e.mood}
                      {e.note && (
                        <span className="ml-2 text-muted-foreground">「{e.note}」</span>
                      )}
                    </div>
                    <div className="text-[10px] text-muted-foreground/80">
                      {new Intl.DateTimeFormat("zh-HK", {
                        hour: "2-digit",
                        minute: "2-digit",
                      }).format(new Date(e.created_at))}
                    </div>
                  </div>
                </StaggerItem>
              );
            })}
          </Stagger>
        )}
      </M3Card>

      {/* Privacy note */}
      <div className="flex items-start gap-2 rounded-2xl bg-secondary/40 px-3 py-2.5 text-[11px] leading-relaxed text-muted-foreground">
        <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />
        打卡資料預設只限你本人。分享畀家人需要你嘅明確授權；FamStatus 唔會收集位置。
      </div>



      {/* Mood picker bottom sheet (formerly FamMood's standalone sheet,
          folded into FamStatus on merge). Same RLS as the rest of
          mood_entries: author RW, so the INSERT only needs the
          current member as author_member_id. */}
      <AnimatePresence>
        {moodPickerOpen && (
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 32 }}
            transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
            role="dialog"
            aria-label="記錄心情"
            className="fixed inset-x-0 bottom-0 z-40 mx-auto max-w-2xl rounded-t-3xl border border-border/60 bg-card p-4 shadow-2xl"
          >
            <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-muted" />
            <div className="mb-1 text-sm font-semibold">揀一個表情</div>
            <p className="mb-3 text-[11px] text-muted-foreground">
              可以加一句心底話，唔加都得。
            </p>
            <div className="grid grid-cols-4 gap-2">
              {MOODS.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setMoodDraft((d) => ({ ...d, id: m.id }))}
                  className={cn(
                    "flex flex-col items-center gap-1 rounded-2xl border p-3 transition-colors",
                    moodDraft.id === m.id
                      ? "border-primary bg-primary/10"
                      : "border-border/60 bg-card hover:bg-secondary",
                  )}
                  aria-pressed={moodDraft.id === m.id}
                >
                  <span aria-hidden className="text-2xl">{m.emoji}</span>
                  <span className="text-[11px]">{m.label}</span>
                </button>
              ))}
            </div>
            <textarea
              value={moodDraft.note}
              onChange={(e) => {
                // Don't read `e.currentTarget.value` from inside the
                // setMoodDraft updater callback. React sets
                // `e.currentTarget` to null once the synchronous
                // onChange handler returns (it's a property defined
                // on the synthetic event itself, not snapshotted),
                // and the updater runs later — by which point the
                // access throws "can't access property 'value',
                // e.currentTarget is null". Capture the value first.
                const value = e.currentTarget.value;
                setMoodDraft((d) => ({ ...d, note: value }));
              }}
              placeholder="（可選）一句心底話…"
              maxLength={240}
              className="mt-3 h-16 w-full resize-none rounded-2xl border border-border/60 bg-background p-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
            />
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                onClick={() => {
                  setMoodPickerOpen(false);
                  setMoodDraft({ id: null, note: "" });
                }}
                className="h-11 flex-1 rounded-full border border-border/60 bg-card text-sm"
              >
                取消
              </button>
              <motion.button
                type="button"
                onClick={logMood}
                disabled={!moodDraft.id || moodSubmitting}
                whileTap={{ scale: 0.96 }}
                transition={{ type: "spring", stiffness: 400, damping: 22 }}
                className="inline-flex h-11 flex-1 items-center justify-center gap-1.5 rounded-full bg-primary text-sm font-medium text-primary-foreground shadow-sm shadow-primary/30 disabled:opacity-50"
              >
                <Smile className="h-4 w-4" aria-hidden />
                {moodSubmitting ? "儲存中…" : "打卡"}
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Past scores sheet. Opens when the user taps a name or
          the "過往記錄" link on a family member card. Renders a
          30-day list of that member's check-ins: date, 3-axis
          wellness bars, and the per-day state. Members that the
          viewer can't see (RLS) just show an empty list — the
          query only returns rows the current user is authorised
          to read (auth.uid() is always in `in (...)` here, so
          everyone in `members` has at least self access). */}
      <AnimatePresence>
        {historySheetMemberId ? (
          <motion.div
            key="history-sheet"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            role="dialog"
            aria-modal="true"
            aria-label="過去打卡記錄"
            className="fixed inset-0 z-40 flex items-end justify-center bg-foreground/40 sm:items-center"
            onClick={() => setHistorySheetMemberId(null)}
          >
            <motion.div
              initial={{ opacity: 0, y: 32 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 32 }}
              transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-2xl rounded-t-3xl border border-border/60 bg-card p-4 shadow-2xl sm:rounded-3xl sm:p-6"
            >
              {(() => {
                const m = members.find(
                  (x) => x.id === historySheetMemberId,
                );
                if (!m) return null;
                // Newest first — the user opens the sheet to answer
                // "how was last week?", not "what was 30 days ago?".
                const rows = [...m.pastCheckIns].sort((a, b) =>
                  a.checked_on < b.checked_on ? 1 : -1,
                );
                return (
                  <>
                    <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-muted sm:hidden" />
                    <div className="mb-4 flex items-center gap-3">
                      <MemberAvatar
                        initials={m.initials}
                        color={m.color}
                        avatarUrl={m.avatarUrl ?? null}
                        size="md"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-base font-semibold">
                          {m.name} · 過去打卡
                        </div>
                        <div className="text-[11px] text-muted-foreground">
                          {m.relation} · 最近 30 日
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setHistorySheetMemberId(null)}
                        className="rounded-full p-1.5 text-muted-foreground hover:bg-secondary"
                        aria-label="關閉"
                      >
                        ×
                      </button>
                    </div>
                    {rows.length === 0 ? (
                      <p className="rounded-2xl bg-secondary/40 px-3 py-6 text-center text-sm text-muted-foreground">
                        過去 30 日冇打卡記錄。
                      </p>
                    ) : (
                      <ul className="max-h-[60vh] space-y-1.5 overflow-y-auto pr-1">
                        {rows.map((r) => (
                          <li
                            key={r.checked_on}
                            className="flex items-center gap-3 rounded-2xl border border-border/40 bg-background/60 px-3 py-2.5"
                          >
                            <div className="w-16 shrink-0 text-[11px] text-muted-foreground">
                              {r.checked_on}
                            </div>
                            <div className="flex flex-1 items-center gap-1.5">
                              <WellnessBar
                                value={r.spirit}
                                color="bg-violet-500"
                                label="精神"
                              />
                              <WellnessBar
                                value={r.body}
                                color="bg-amber-500"
                                label="體力"
                              />
                              <WellnessBar
                                value={r.mood}
                                color="bg-rose-500"
                                label="心情"
                              />
                            </div>
                            <span
                              className={cn(
                                "shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold",
                                r.state === "done" &&
                                  "bg-emerald-50 text-emerald-700",
                                r.state !== "done" &&
                                  "bg-rose-50 text-rose-700",
                              )}
                            >
                              {r.state === "done" ? "已打卡" : r.state}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                );
              })()}
            </motion.div>
          </motion.div>
        ) : null}
      </AnimatePresence>
     </div>
  );
}

/* Single axis bar (e.g. 精神). `value` is 1..5 from check_ins;
   `null` means the row didn't record a score (only state) — the
   bar renders as a thin dashed track to make the missing data
   visible instead of pretending to be 0/3. */
function WellnessBar({
  value,
  color,
  label,
}: {
  value: number | null;
  color: string;
  label: string;
}) {
  const v = value ?? 0;
  return (
    <div
      className="flex flex-1 items-center gap-1"
      aria-label={value == null ? `${label} 冇資料` : `${label} ${v} / 5`}
    >
      <span className="w-6 shrink-0 text-[9px] font-medium text-muted-foreground">
        {label}
      </span>
      <span className="relative h-1.5 w-full overflow-hidden rounded-full bg-border/40">
        {value != null ? (
          <span
            className={cn("absolute inset-y-0 left-0 rounded-full", color)}
            style={{ width: `${(v / 5) * 100}%` }}
          />
        ) : (
          <span
            aria-hidden
            className="absolute inset-0 bg-[repeating-linear-gradient(45deg,transparent_0_3px,currentColor_3px_5px)] text-border"
          />
        )}
      </span>
    </div>
  );
}

export default FamStatusView;
